import { observer } from 'mobx-react';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import deliveryZonesStore from '../../stores/deliveryZonesStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme, { PALETTE_COLORS, ZONE_UNAVAILABLE_COLOR } from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IBranch } from 'src/types/branches';
import { DELIVERY_ZONE_TYPE, IDeliveryZone } from 'src/types/deliveryManagment';
import { TBooleanInNumber } from 'src/types/main';
import { booleanToNumber, getTranslate, scrollToErrorElement, stringTransform } from 'src/utils/helpers';
import { confirmDelete, leaveWithoutSaving } from 'src/utils/modalConfirmations';
import { throttle } from 'src/utils/perfomance';
import { floatNumbersValidation } from 'src/utils/validation';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import Palette from 'src/view/components/Controls/ColorPalette/ColorPalette';
import { HintBox } from 'src/view/components/Controls/HintBox/HintBox';
import Icon from 'src/view/components/Controls/Icon/Icon';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import FakePicker from 'src/view/components/Controls/Pickers/FakePicker/FakePicker';
import Sizes from 'src/view/components/Controls/Sizes/Sizes';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { getRandomIntNumber } from '~/helpers';
import askAgain, { AskAgainKey } from '~/services/askAgain';
import './style.scss';

enum FORM_INPUTS {
  NAME = 'name',
  RADIUS = 'radius',
  EXTERNAL_ID = 'external_id',
}

interface Props {
  zone: IDeliveryZone;
  onCancel: (zone: IDeliveryZone) => void;
  onDelete: (zone: IDeliveryZone) => void;
  onSave: (zone: IDeliveryZone) => void;
}

export const DeliveryZonesForm = observer((props: Props) => {
  const [hintBox, setHintBox] = useState<boolean>(!Boolean(deliveryZonesStore.draft?.branch));
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    deliveryZonesStore.setValidation(true);
  }, []);

  // useEffect(() => {
  //   if ([FORM_MODE.CREATE, FORM_MODE.EDIT].includes(deliveryZonesStore.mode)) {
  //     setVisibility('animate__fadeInRight');
  //   } else if ([FORM_MODE.HIDDEN].includes(deliveryZonesStore.mode)) {
  //     setVisibility('animate__fadeOutRight');
  //   } else {
  //     setVisibility('hidden');
  //   }
  // }, [deliveryZonesStore.mode]);

  const changeZoneStatus = (status: TBooleanInNumber) => {
    if (!deliveryZonesStore.draft) return;

    deliveryZonesStore.editDraftZone('status', status);

    // add color
    if (deliveryZonesStore.draft.status && deliveryZonesStore.draft.color === ZONE_UNAVAILABLE_COLOR) {
      deliveryZonesStore.editDraftZone('color', PALETTE_COLORS[getRandomIntNumber(0, PALETTE_COLORS.length - 1)]);
    }
  };

  const changeInputStatus = (event: ChangeEvent<HTMLInputElement>) => {
    if (!deliveryZonesStore.draft) return;

    if (event.target.name === FORM_INPUTS.NAME) {
      deliveryZonesStore.editDraftZone(FORM_INPUTS.NAME, {
        en: event.target.value,
        ar: event.target.value,
      });
      // update validation
      if (deliveryZonesStore.validation.name && event.target.value) deliveryZonesStore.setValidation();
      // update tooltip
      deliveryZonesStore.selectZone(deliveryZonesStore.draft);
    } else if (event.target.name === FORM_INPUTS.RADIUS) {
      const radius: string = stringTransform(event.target.value);
      if (!floatNumbersValidation(radius, 1)) return;
      changeRadius(radius);
    } else {
      deliveryZonesStore.editDraftZone(event.target.name, event.target.value);
    }
  };

  const changeRadius = (value: string) => {
    if (!deliveryZonesStore.draft) return;
    deliveryZonesStore.editDraftZone(FORM_INPUTS.RADIUS, value || '');
    // update vaildation
    if (deliveryZonesStore.validation.radius && value) deliveryZonesStore.setValidation();
    // change zoom and update tooltip
    deliveryZonesStore.selectZone(deliveryZonesStore.draft);
  };

  const changeZoneType = (type: DELIVERY_ZONE_TYPE) => {
    if (type === DELIVERY_ZONE_TYPE.PLYGON && askAgain.enabledHintMessage[AskAgainKey.DRAW_POLYGON] && !deliveryZonesStore.draft?.bounds.length) {
      deliveryZonesStore.changeDrawHintVisibility(true);
    } else {
      deliveryZonesStore.changeDrawHintVisibility(false);
    }

    if (type === DELIVERY_ZONE_TYPE.RADIUS && !deliveryZonesStore.draft?.radius) changeRadius('3');

    deliveryZonesStore.editDraftZone('type', type);
    // change zoom, map state and update tooltip
    deliveryZonesStore.selectZone(deliveryZonesStore.draft);
  };

  const changeBranch = (branch: IBranch) => {
    if (!deliveryZonesStore.draft || !branch) return;

    // add color
    if (deliveryZonesStore.draft.color === ZONE_UNAVAILABLE_COLOR) {
      deliveryZonesStore.editDraftZone('color', PALETTE_COLORS[getRandomIntNumber(0, PALETTE_COLORS.length - 1)]);
    }
    if (!deliveryZonesStore.draft.status_in_branch) {
      deliveryZonesStore.editDraftZone('status_in_branch', 1);
    }

    deliveryZonesStore.editDraftZone('branch', {
      id: branch.id,
      external_id: branch.external_id,
      name: branch.name,
      latitude: branch.latitude,
      longitude: branch.longitude,
    });
    setHintBox(false);

    // update vaildation
    if (deliveryZonesStore.validation.branch) deliveryZonesStore.setValidation();
    // change map state and update tooltip
    deliveryZonesStore.selectZone(deliveryZonesStore.draft);

    // if (deliveryZonesStore.draft?.type === DELIVERY_ZONE_TYPE.PLYGON && deliveryZonesStore.draft.bounds.length) {
    //   deliveryZonesStore.checkBranchCoverage();
    // }
  };

  const changeZoneColor = (color: string) => {
    deliveryZonesStore.editDraftZone('color', color);
  };

  const deleteZone = async () => {
    try {
      setLoading(true);
      await deliveryZonesStore.deleteZone(props.zone);
      deliveryZonesStore.clearDraft();
    } finally {
      setLoading(false);
    }
  };
  const onDelete = useCallback(
    throttle(() => {
      confirmDelete({ message: localeStore.t('confirm_zone_delete'), onConfirm: deleteZone, redConfirmBtn: true });
    }, 1000),
    [props.zone]
  );

  const onCancel = useCallback(() => {
    leaveWithoutSaving({ onConfirm: deliveryZonesStore.clearDraft });
  }, []);

  const openBranchPicker = () => {
    modalStore.openBranchesModal({
      selectedBranches: deliveryZonesStore.draft?.branch ? [deliveryZonesStore.draft.branch] : [],
      isMulti: false,
      // isZeroBranches: true,
      busy: false,
      onSave: (branches, branchesFull) => {
        modalStore.closeBranchesModal();
        if (branchesFull?.length) {
          changeBranch(branchesFull[0]);
        }
      },
    });
  };

  const onSubmit = throttle(async () => {
    try {
      deliveryZonesStore.setValidation();

      if (Object.values(deliveryZonesStore.validation).some((validation) => validation)) {
        scrollToErrorElement();
        return;
      }

      setLoading(true);
      if (!deliveryZonesStore.draft) return;
      // if id is not 0 that's mean zone is already exist
      if (deliveryZonesStore.draft?.id) {
        await deliveryZonesStore.updateZone(deliveryZonesStore.draft);
      } else {
        await deliveryZonesStore.createZone(deliveryZonesStore.draft);
      }
    } finally {
      setLoading(false);
    }
  }, 1000);

  const switcherDisabled = (): boolean => {
    if (!deliveryZonesStore.draft?.status) {
      if (!deliveryZonesStore.draft?.branch) return true;
      if (deliveryZonesStore.draft?.type === DELIVERY_ZONE_TYPE.RADIUS && !deliveryZonesStore.draft?.radius) return true;
      if (deliveryZonesStore.draft?.type === DELIVERY_ZONE_TYPE.PLYGON && !deliveryZonesStore.draft?.bounds.length) return true;
    }
    return false;
  };

  if (!deliveryZonesStore.draft) return null;

  return (
    <div className="delivery-zone__form animate__animated animate__faster animate__fadeInRight">
      <div className="d-flex flex-1 flex-column">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span onClick={onCancel}>
            <Icon name={Icons.Back} color={theme.colors.mainblue} className="delivery-zone__icon-back cursor-pointer" size={24} />
          </span>

          {deliveryZonesStore.draft.id ? (
            <span onClick={onDelete}>
              <Icon name={Icons.Trash} size={24} color={theme.colors.dark} className="cursor-pointer" />
            </span>
          ) : null}
        </div>

        <ScrollableBlock>
          <div className="d-flex align-items-center mb-4">
            <Switcher disabled={switcherDisabled()} status={booleanToNumber(deliveryZonesStore.draft.status)} onChange={changeZoneStatus} />
            <p className="text-size-20 family-bold text-elipsis ml-3">{getTranslate(deliveryZonesStore.draft.name)}</p>
          </div>

          <InputAlt
            required
            className="bg-transparent"
            value={getTranslate(deliveryZonesStore.draft.name)}
            onChange={changeInputStatus}
            name={FORM_INPUTS.NAME}
            placeholder={localeStore.t('txt_waitlist_name')}
            validation={{
              state: !deliveryZonesStore.validation.name,
              errorText: localeStore.t(deliveryZonesStore.validation.nameErrorMessage || ''),
              styleType: 'bottom',
            }}
          />

          <HintBox open={hintBox} minTop={215} minBottom={80} text="select_branch_zone" onClick={() => setHintBox(!hintBox)}>
            <FakePicker
              value={getTranslate(deliveryZonesStore.draft.branch?.name)}
              onClick={openBranchPicker}
              error={deliveryZonesStore.validation.branch}
              className="bg-transparent flex-row my-3 full-width px-4"
              placeholderClassName="family-medium required"
              textClassName={`family-medium ${deliveryZonesStore.draft.branch?.is_delete ? 'text-through' : ''}`}
              placeholder={localeStore.t('select_branch')}
            />
          </HintBox>

          <InputAlt
            className="bg-transparent mb-3"
            value={deliveryZonesStore.draft.external_id}
            onChange={changeInputStatus}
            name={FORM_INPUTS.EXTERNAL_ID}
            placeholder={localeStore.t('external_id')}
          />
          <div className="mb-3">
            <Sizes<DELIVERY_ZONE_TYPE>
              onChange={changeZoneType}
              disabled={!deliveryZonesStore.draft.branch}
              selected={deliveryZonesStore.draft.branch ? deliveryZonesStore.draft.type : null}
              options={[
                { value: DELIVERY_ZONE_TYPE.RADIUS, key: 0, name: localeStore.t('radius') },
                { value: DELIVERY_ZONE_TYPE.PLYGON, key: 1, name: localeStore.t('hint_area') },
              ]}
            />
          </div>

          {deliveryZonesStore.draft.type === DELIVERY_ZONE_TYPE.RADIUS && deliveryZonesStore.draft.branch ? (
            <InputAlt
              required
              type="text"
              className={`bg-transparent input-number mb-3 ${deliveryZonesStore.validation.radius ? 'input-error' : ''}`}
              // space required !!!
              value={deliveryZonesStore.draft.radius?.toString() || ' '}
              onInput={changeInputStatus}
              name={FORM_INPUTS.RADIUS}
              placeholder={localeStore.t('radius_in_km')}
              disabled={loading}
            />
          ) : null}

          <Palette onSelect={changeZoneColor} selected={deliveryZonesStore.draft.color} />
        </ScrollableBlock>
      </div>
      <Button title={localeStore.t('txt_save')} onClick={onSubmit} disabled={loading} loading={loading} size="large" stretched className="mt-3" />
    </div>
  );
});
